import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import {
  Loader2, Save, Play, FileText, Sparkles, X, Send, Trash2, Copy, Check, ArrowLeft, Eye, Pencil, Paperclip,
} from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { getFrsContent, saveFrsContent, generateAppFromFrs, enhanceFrsWithAi, getJson } from '@/api/generateapp'
import { saveDraft } from '@/api/workbench'
import { getSettings } from '@/api/settings'
import { useProjectStore } from '@/store/projectStore'
import { useClaudeAgent } from '@/hooks/useClaudeAgent'
import { useAttachments } from '@/hooks/useAttachments'
import AttachmentChips from '@/components/common/AttachmentChips'
import { ATTACHMENT_ACCEPT, toWire } from '@/lib/attachments'
import AgentStreamPanel from '@/components/common/AgentStreamPanel'

interface ChatMessage {
  role: 'user' | 'assistant'
  content: string
  modifiedDocument?: string  // Stored for "Apply to Document" action
  detail?: string            // Processing detail (mode, edits applied/skipped)
  noChanges?: boolean        // True when AI returned same document as original
}

export default function FinetuneFrsPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const project = searchParams.get('project') || 'Draft Application'
  const { currentProject, selectProject } = useProjectStore()

  // Restore currentProject in store from URL param on refresh
  useEffect(() => {
    if (project && project !== 'Draft Application' && currentProject?.application.folder_name !== project) {
      selectProject(project)
    }
  }, [project, currentProject, selectProject])

  const [frsContent, setFrsContent] = useState('')
  const [originalContent, setOriginalContent] = useState('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [generatingApp, setGeneratingApp] = useState(false)
  const [showDiscardModal, setShowDiscardModal] = useState(false)
  const [editMode, setEditMode] = useState<'edit' | 'preview'>('edit')
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  // AI Assistant state
  const [aiPanelOpen, setAiPanelOpen] = useState(false)
  const [aiInput, setAiInput] = useState('')
  const att = useAttachments()
  const [aiLoading, setAiLoading] = useState(false)
  const [selectedText, setSelectedText] = useState('')
  const [chatHistory, setChatHistory] = useState<ChatMessage[]>([])
  const chatEndRef = useRef<HTMLDivElement>(null)
  const [generationMode, setGenerationMode] = useState<string>('llm')
  const [effortConfig, setEffortConfig] = useState<Record<string, string>>({})

  // Load generation mode and thinking effort
  const loadGenerationMode = useCallback(() => {
    getSettings().then((s) => {
      setGenerationMode(s.generation_mode || 'llm')
      if (s.thinking_effort_config) setEffortConfig(s.thinking_effort_config)
    }).catch(() => { /* use default */ })
  }, [])

  useEffect(() => {
    loadGenerationMode()
  }, [loadGenerationMode])

  // Track which agent task is active (enhance_frs vs app_json)
  const agentTaskTypeRef = useRef<string>('enhance_frs')
  const [agentAppPanelOpen, setAgentAppPanelOpen] = useState(false)

  // Claude Code Agent for FRS enhancement and app generation
  const agent = useClaudeAgent({
    project,
    onComplete: async (changedFiles) => {
      if (agentTaskTypeRef.current === 'app_json') {
        const appFile = changedFiles.find((f) => f.path.includes('application.json'))
        if (appFile) {
          toast.success('Application generated by Claude Agent!')
          setGeneratingApp(false)
          setAgentAppPanelOpen(false)
          try {
            const appData = await getJson(project)
            const appId = appData?.application?.id
            if (appId && appId !== project && project === 'Draft Application') {
              await saveDraft(appId)
              navigate(`/generateapp/preview?project=${encodeURIComponent(appId)}`)
            } else {
              navigate(`/generateapp/preview?project=${encodeURIComponent(project)}`)
            }
          } catch {
            navigate(`/generateapp/preview?project=${encodeURIComponent(project)}`)
          }
        } else {
          toast.error('Application generation completed but no file was created')
          setGeneratingApp(false)
        }
        return
      }
      // FRS enhancement task
      const frsFile = changedFiles.find((f) => f.path === 'requirement_doc.md')
      if (frsFile?.content) {
        setFrsContent(frsFile.content)
        setOriginalContent(frsFile.content)
        toast.success('FRS updated by AI agent')
      }
    },
  })

  useEffect(() => {
    const loadFrs = async () => {
      setLoading(true)
      try {
        const data = await getFrsContent(project)
        setFrsContent(data.content || '')
        setOriginalContent(data.content || '')
      } catch {
        toast.error('Failed to load FRS content')
      } finally {
        setLoading(false)
      }
    }
    loadFrs()
  }, [project])

  // Scroll chat to bottom when new messages added
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [chatHistory])

  const getEditorContent = useCallback((): string => {
    return frsContent
  }, [frsContent])

  const hasUnsavedChanges = useCallback((): boolean => {
    return frsContent !== originalContent
  }, [frsContent, originalContent])

  const handleBack = useCallback(() => {
    if (hasUnsavedChanges()) {
      setShowDiscardModal(true)
    } else {
      navigate('/workbench')
    }
  }, [hasUnsavedChanges, navigate])

  const handleDiscard = useCallback(() => {
    setShowDiscardModal(false)
    navigate('/workbench')
  }, [navigate])

  const handleSaveFrs = useCallback(async () => {
    setSaving(true)
    try {
      const content = getEditorContent()
      await saveFrsContent(content, project)
      toast.success('FRS saved successfully')
    } catch {
      toast.error('Failed to save FRS')
    } finally {
      setSaving(false)
    }
  }, [getEditorContent, project])

  const handleGenerateApp = useCallback(async () => {
    // Save current content first so the agent/API reads the latest
    try {
      const content = getEditorContent()
      await saveFrsContent(content, project)
      setOriginalContent(content)
    } catch {
      toast.error('Failed to save FRS before generation')
      return
    }

    // Claude Code Agent mode
    if (generationMode === 'claude_code') {
      agentTaskTypeRef.current = 'app_json'
      setGeneratingApp(true)
      setAgentAppPanelOpen(true)
      agent.startTask('app_json', {
        description: 'Generate application JSON from FRS',
        effort: effortConfig.app_json,
      })
      return
    }

    // LLM API mode (existing behavior)
    setGeneratingApp(true)
    try {
      const content = getEditorContent()
      await generateAppFromFrs(content, project)
      toast.success('Application generated from FRS!')
      navigate(`/generateapp/preview?project=${encodeURIComponent(project)}`)
    } catch {
      toast.error('Failed to generate application from FRS')
    } finally {
      setGeneratingApp(false)
    }
  }, [getEditorContent, project, navigate, generationMode, agent, originalContent, effortConfig])

  // Capture selected text from the editor
  const captureSelection = useCallback(() => {
    if (editMode === 'edit' && textareaRef.current) {
      const { selectionStart, selectionEnd } = textareaRef.current
      if (selectionStart !== selectionEnd) {
        setSelectedText(frsContent.slice(selectionStart, selectionEnd).trim())
        return
      }
    }
    const selection = window.getSelection()
    if (selection && selection.toString().trim()) {
      setSelectedText(selection.toString().trim())
    }
  }, [editMode, frsContent])

  const openAiAssistant = useCallback(() => {
    captureSelection()
    loadGenerationMode()  // Re-fetch settings to ensure latest mode
    setAiPanelOpen(true)
  }, [captureSelection, loadGenerationMode])

  const handleAiSubmit = useCallback(async () => {
    const userInput = aiInput.trim()
    const wireAttachments = att.attachments.map(toWire)
    if (!userInput && wireAttachments.length === 0) {
      toast.error('Please enter an instruction or attach a file for the AI')
      return
    }

    // Get current selection if not already captured
    const sel = selectedText || window.getSelection()?.toString().trim() || ''

    // Claude Code Agent mode
    if (generationMode === 'claude_code') {
      agentTaskTypeRef.current = 'enhance_frs'
      setAiInput('')
      // Save current editor content to disk so the agent reads the latest version
      try {
        await saveFrsContent(frsContent, project)
        setOriginalContent(frsContent)
      } catch {
        toast.error('Failed to save FRS before AI enhancement')
        return
      }
      agent.startTask('enhance_frs', {
        selected_text: sel,
        user_input: userInput,
        full_document: frsContent,
        description: userInput,
        effort: effortConfig.enhance_frs,
        ...(wireAttachments.length ? { attachments: wireAttachments } : {}),
      })
      att.clear()
      setSelectedText('')
      return
    }

    // LLM API mode
    const userMessage: ChatMessage = {
      role: 'user',
      content: sel
        ? `**Instruction:** ${userInput}\n\n**Selected text:**\n${sel.slice(0, 200)}${sel.length > 200 ? '...' : ''}`
        : userInput,
    }

    setChatHistory((prev) => [...prev, userMessage])
    setAiInput('')
    setAiLoading(true)

    try {
      const res = await enhanceFrsWithAi({
        user_input: userInput,
        selected_text: sel,
        full_document: frsContent,
        file_path: 'frs.md',
        ...(wireAttachments.length ? { attachments: wireAttachments } : {}),
      })
      att.clear()

      // New format: {modified_document, summary, detail} or legacy {code}
      if (res.modified_document !== undefined) {
        // Check if the document actually changed
        const docChanged = res.modified_document !== frsContent
        const assistantMessage: ChatMessage = {
          role: 'assistant',
          content: docChanged
            ? (res.summary || 'Changes ready to apply.')
            : (res.summary || 'No changes were made to the document.'),
          modifiedDocument: docChanged ? res.modified_document : undefined,
          detail: res.detail || undefined,
          noChanges: !docChanged,
        }
        setChatHistory((prev) => [...prev, assistantMessage])
      } else {
        // Legacy fallback
        const assistantMessage: ChatMessage = {
          role: 'assistant',
          content: res.code || res.enhanced_specification || 'No response from AI',
        }
        setChatHistory((prev) => [...prev, assistantMessage])
      }
    } catch {
      toast.error('AI enhancement failed')
      setChatHistory((prev) => [
        ...prev,
        { role: 'assistant', content: 'Error: Failed to get AI response.' },
      ])
    } finally {
      setAiLoading(false)
      setSelectedText('')
    }
  }, [aiInput, selectedText, frsContent, generationMode, agent, att])

  const handleAcceptResponse = useCallback((content: string, modifiedDocument?: string) => {
    if (modifiedDocument) {
      // Full document replacement from AI edit-instruction result
      setFrsContent(modifiedDocument)
      toast.success('Document updated with AI changes')
    } else if (editMode === 'edit' && textareaRef.current) {
      const { selectionStart, selectionEnd } = textareaRef.current
      if (selectionStart !== selectionEnd) {
        const before = frsContent.slice(0, selectionStart)
        const after = frsContent.slice(selectionEnd)
        setFrsContent(before + content + after)
      } else {
        setFrsContent((prev) => prev + '\n' + content)
      }
      toast.success('Content applied to editor')
    } else {
      setFrsContent((prev) => prev + '\n' + content)
      toast.success('Content applied to editor')
    }
  }, [editMode, frsContent])

  const handleCopyResponse = useCallback((content: string) => {
    navigator.clipboard.writeText(content)
    toast.success('Copied to clipboard')
  }, [])

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleAiSubmit()
    }
  }, [handleAiSubmit])

  if (loading) {
    return (
      <div className="flex items-center justify-center" style={{ height: '100%' }}>
        <div className="flex flex-col items-center gap-3">
          <Loader2 size={32} style={{ animation: 'spin 1s linear infinite', color: 'var(--color-accent)' }} />
          <span style={{ color: 'var(--color-text-secondary)' }}>Loading FRS content...</span>
        </div>
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flex: 1, minHeight: 0, gap: 0 }}>
      {/* Main editor area */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 0 1rem 0',
          borderBottom: '1px solid var(--color-border)',
          marginBottom: '1rem',
          flexShrink: 0,
        }}>
          <div className="flex items-center gap-3">
            <button
              className="btn btn-ghost btn-sm"
              onClick={handleBack}
              title="Back to Workbench"
              style={{ padding: '4px 8px', flexShrink: 0 }}
            >
              <ArrowLeft size={18} />
            </button>
            <FileText size={22} style={{ color: 'var(--color-accent)' }} />
            <div>
              <h2 style={{ fontSize: '1.3125rem', fontWeight: 600, marginBottom: 2 }}>
                Fine-tune Requirements
              </h2>
              <p style={{ color: 'var(--color-text-secondary)', fontSize: '0.875rem', margin: 0 }}>
                Edit the specification, use AI Assistant to enhance sections
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="badge badge-info" style={{ fontSize: '0.8125rem' }}>
              {project}
            </span>
            <button
              className={`btn ${aiPanelOpen ? 'btn-primary' : 'btn-secondary'}`}
              onClick={openAiAssistant}
              title="Select text in the editor, then click to enhance with AI"
            >
              <Sparkles size={16} />
              AI Assistant
            </button>
          </div>
        </div>

        {/* Edit/Preview toggle */}
        <div style={{
          display: 'flex',
          gap: '0.25rem',
          marginBottom: '0.5rem',
          flexShrink: 0,
        }}>
          <button
            className={`btn btn-sm ${editMode === 'edit' ? 'btn-primary' : 'btn-ghost'}`}
            onClick={() => setEditMode('edit')}
            style={{ padding: '4px 12px', fontSize: '0.875rem' }}
          >
            <Pencil size={14} />
            Edit
          </button>
          <button
            className={`btn btn-sm ${editMode === 'preview' ? 'btn-primary' : 'btn-ghost'}`}
            onClick={() => setEditMode('preview')}
            style={{ padding: '4px 12px', fontSize: '0.875rem' }}
          >
            <Eye size={14} />
            Preview
          </button>
        </div>

        {/* Editor / Preview */}
        <div style={{
          flex: 1,
          overflow: 'auto',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius-lg)',
          background: 'var(--color-bg-secondary)',
          minHeight: 0,
        }}>
          {editMode === 'edit' ? (
            <textarea
              ref={textareaRef}
              value={frsContent}
              onChange={(e) => setFrsContent(e.target.value)}
              onMouseUp={captureSelection}
              style={{
                width: '100%',
                height: '100%',
                padding: '1.5rem',
                border: 'none',
                outline: 'none',
                background: 'transparent',
                color: 'var(--color-text)',
                fontSize: '1rem',
                lineHeight: 1.7,
                fontFamily: "'SF Mono', 'Fira Code', monospace",
                resize: 'none',
              }}
            />
          ) : (
            <div
              className="markdown-content"
              onMouseUp={captureSelection}
              style={{
                padding: '1.5rem',
                minHeight: '100%',
              }}
            >
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  // Highlight [ref: AppName → TABLE] annotations as inline badges
                  strong: ({ children, ...props }) => {
                    const text = typeof children === 'string' ? children :
                      Array.isArray(children) ? children.map(c => typeof c === 'string' ? c : '').join('') : ''
                    if (text && /^\[ref:\s*.+\]$/.test(text)) {
                      return (
                        <span
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 3,
                            padding: '1px 6px',
                            fontSize: '0.75em',
                            background: 'rgba(0, 153, 116, 0.12)',
                            color: '#009974',
                            borderRadius: 4,
                            border: '1px solid rgba(0, 153, 116, 0.25)',
                            fontWeight: 600,
                            fontFamily: 'var(--font-primary)',
                            verticalAlign: 'middle',
                          }}
                          title="Cross-application reference"
                        >
                          {text}
                        </span>
                      )
                    }
                    return <strong {...props}>{children}</strong>
                  },
                }}
              >
                {frsContent}
              </ReactMarkdown>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '0.75rem',
          paddingTop: '1rem',
          borderTop: '1px solid var(--color-border)',
          marginTop: '1rem',
          flexShrink: 0,
        }}>
          <button
            className="btn btn-secondary"
            onClick={handleBack}
            disabled={saving || generatingApp}
          >
            <X size={16} />
            Discard
          </button>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button
              className="btn btn-secondary"
              onClick={handleSaveFrs}
              disabled={saving || generatingApp}
            >
              {saving ? (
                <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
              ) : (
                <Save size={16} />
              )}
              {saving ? 'Saving...' : 'Save FRS'}
            </button>
            <button
              className="btn btn-primary"
              onClick={handleGenerateApp}
              disabled={saving || generatingApp}
            >
              {generatingApp ? (
                <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
              ) : (
                <Play size={16} />
              )}
              {generatingApp ? 'Generating...' : 'Re-generate App'}
            </button>
          </div>
        </div>
      </div>

      {/* Discard Confirmation Modal */}
      {showDiscardModal && (
        <div className="overlay">
          <div className="modal" style={{ maxWidth: 420 }}>
            <div className="modal-header">
              <h3 className="modal-title">Discard Changes?</h3>
              <button className="modal-close" onClick={() => setShowDiscardModal(false)}>
                <span className="material-icons">close</span>
              </button>
            </div>
            <p style={{
              fontSize: '0.9375rem',
              color: 'var(--color-text-secondary)',
              marginBottom: '1.25rem',
              lineHeight: 1.7,
            }}>
              You have unsaved changes to the specification. Are you sure you want to discard them and go back?
            </p>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setShowDiscardModal(false)}
              >
                Keep Editing
              </button>
              <button
                className="btn btn-danger"
                onClick={handleDiscard}
              >
                <Trash2 size={16} />
                Discard Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* AI Assistant Panel */}
      {aiPanelOpen && (
        <div style={{
          width: 380,
          flexShrink: 0,
          borderLeft: '1px solid var(--color-border)',
          background: 'var(--color-bg-secondary)',
          display: 'flex',
          flexDirection: 'column',
          marginLeft: '1rem',
          borderRadius: 'var(--radius-lg)',
          overflow: 'hidden',
          animation: 'slideInRight 200ms ease-out',
        }}>
          {/* Claude Code Agent mode */}
          {generationMode === 'claude_code' ? (
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              <AgentStreamPanel
                messages={agent.messages}
                isStreaming={agent.isStreaming}
                wsStatus={agent.wsStatus}
                error={agent.error}
                agentProgress={agent.agentProgress}
                onCancel={agent.cancelTask}
                onClear={agent.clearMessages}
                onClose={() => setAiPanelOpen(false)}
                onPermissionResponse={agent.respondToPermission}
                title="AI Assistant"
              />
              {/* Input area for agent mode */}
              <div style={{
                padding: '0.75rem',
                borderTop: '1px solid var(--color-border)',
                flexShrink: 0,
              }}>
                {selectedText && (
                  <div style={{
                    padding: '0.375rem 0.5rem',
                    marginBottom: '0.5rem',
                    background: 'rgba(76, 141, 255, 0.08)',
                    borderRadius: 'var(--radius-sm)',
                    fontSize: '0.8125rem',
                    color: 'var(--color-text-secondary)',
                  }}>
                    <strong style={{ color: 'var(--color-accent)' }}>Selected:</strong> {selectedText.slice(0, 100)}{selectedText.length > 100 ? '...' : ''}
                  </div>
                )}
                <AttachmentChips attachments={att.attachments} onRemove={att.remove} disabled={agent.isStreaming} />
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <input
                    ref={att.fileInputRef}
                    type="file"
                    multiple
                    accept={ATTACHMENT_ACCEPT}
                    onChange={att.onFileInputChange}
                    style={{ display: 'none' }}
                  />
                  <button
                    type="button"
                    className="btn btn-secondary btn-icon"
                    onClick={att.openFilePicker}
                    disabled={agent.isStreaming}
                    title="Attach files or images"
                    style={{ width: 36, height: 36, flexShrink: 0, alignSelf: 'flex-end' }}
                  >
                    <Paperclip size={14} />
                  </button>
                  <textarea
                    value={aiInput}
                    onChange={(e) => setAiInput(e.target.value)}
                    {...att.inputHandlers}
                    onKeyDown={handleKeyDown}
                    placeholder="e.g. Make this section more detailed..."
                    rows={2}
                    disabled={agent.isStreaming}
                    style={{
                      flex: 1, resize: 'none', padding: '0.5rem 0.75rem', fontSize: '0.875rem',
                      background: 'var(--color-bg)', border: '1px solid var(--color-border)',
                      borderRadius: 'var(--radius-md)', color: 'var(--color-text)',
                      fontFamily: 'var(--font-primary)', lineHeight: 1.4,
                    }}
                  />
                  <button
                    className="btn btn-primary btn-icon"
                    onClick={handleAiSubmit}
                    disabled={agent.isStreaming || (!aiInput.trim() && att.attachments.length === 0)}
                    style={{ width: 36, height: 36, flexShrink: 0, alignSelf: 'flex-end' }}
                  >
                    <Send size={14} />
                  </button>
                </div>
              </div>
            </div>
          ) : (
            /* LLM API mode (existing behavior) */
            <>
              {/* Panel header */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '0.875rem 1rem',
                borderBottom: '1px solid var(--color-border)',
                flexShrink: 0,
              }}>
                <div className="flex items-center gap-2">
                  <Sparkles size={16} style={{ color: 'var(--color-accent)' }} />
                  <span style={{ fontWeight: 600, fontSize: '1rem' }}>AI Assistant</span>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={() => setChatHistory([])}
                    title="Clear history"
                    style={{ padding: '4px 6px' }}
                  >
                    <Trash2 size={14} />
                  </button>
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={() => setAiPanelOpen(false)}
                    style={{ padding: '4px 6px' }}
                  >
                    <X size={16} />
                  </button>
                </div>
              </div>

              {/* Selected text indicator */}
              {selectedText && (
                <div style={{
                  padding: '0.5rem 1rem',
                  background: 'rgba(76, 141, 255, 0.08)',
                  borderBottom: '1px solid var(--color-border)',
                  flexShrink: 0,
                }}>
                  <div style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--color-accent)', marginBottom: 2, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Selected text
                  </div>
                  <p style={{
                    fontSize: '0.8125rem',
                    color: 'var(--color-text-secondary)',
                    margin: 0,
                    maxHeight: 60,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    lineHeight: 1.4,
                  }}>
                    {selectedText.slice(0, 200)}{selectedText.length > 200 ? '...' : ''}
                  </p>
                </div>
              )}

              {/* Chat history */}
              <div style={{
                flex: 1,
                overflow: 'auto',
                padding: '0.75rem',
                display: 'flex',
                flexDirection: 'column',
                gap: '0.75rem',
                minHeight: 0,
              }}>
                {chatHistory.length === 0 && (
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flex: 1,
                    gap: '0.5rem',
                    padding: '2rem',
                  }}>
                    <Sparkles size={28} style={{ color: 'var(--color-text-secondary)', opacity: 0.3 }} />
                    <p style={{ color: 'var(--color-text-secondary)', fontSize: '0.875rem', textAlign: 'center', margin: 0 }}>
                      Describe what to change in your document. Optionally select text to focus the AI on a specific section.
                    </p>
                  </div>
                )}

                {chatHistory.map((msg, idx) => (
                  <div key={idx} style={{
                    padding: '0.75rem',
                    borderRadius: 'var(--radius-md)',
                    background: msg.role === 'user'
                      ? 'rgba(0, 97, 208, 0.08)'
                      : 'var(--color-bg)',
                    border: `1px solid ${msg.role === 'user' ? 'rgba(0, 97, 208, 0.15)' : 'var(--color-border)'}`,
                  }}>
                    <div style={{
                      fontSize: '0.8125rem',
                      fontWeight: 600,
                      color: msg.role === 'user' ? '#0061D0' : 'var(--color-accent)',
                      marginBottom: '0.375rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.04em',
                    }}>
                      {msg.role === 'user' ? 'You' : 'AI Assistant'}
                    </div>
                    <div style={{
                      fontSize: '0.875rem',
                      lineHeight: 1.6,
                      color: 'var(--color-text)',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    }}>
                      {msg.content}
                    </div>
                    {/* Detail info (mode, edits applied/skipped) */}
                    {msg.detail && (
                      <div style={{
                        fontSize: '0.8125rem',
                        color: 'var(--color-text-secondary)',
                        marginTop: '0.375rem',
                        padding: '0.25rem 0.5rem',
                        background: 'rgba(128,128,128,0.08)',
                        borderRadius: 'var(--radius-sm)',
                        fontFamily: "'SF Mono', 'Fira Code', monospace",
                      }}>
                        {msg.detail}
                      </div>
                    )}
                    {/* No-changes warning */}
                    {msg.role === 'assistant' && msg.noChanges && (
                      <div style={{
                        fontSize: '0.8125rem',
                        color: '#e6a700',
                        marginTop: '0.375rem',
                        fontWeight: 500,
                      }}>
                        No changes detected in the document. Try rephrasing your instruction.
                      </div>
                    )}
                    {msg.role === 'assistant' && !msg.content.startsWith('Error:') && !msg.noChanges && (
                      <div style={{
                        display: 'flex',
                        gap: '0.375rem',
                        marginTop: '0.5rem',
                        paddingTop: '0.5rem',
                        borderTop: '1px solid var(--color-border)',
                      }}>
                        {msg.modifiedDocument && (
                          <button
                            className="btn btn-ghost btn-sm"
                            onClick={() => handleAcceptResponse(msg.content, msg.modifiedDocument)}
                            style={{
                              fontSize: '0.8125rem',
                              padding: '2px 8px',
                              color: 'var(--color-accent)',
                              fontWeight: 600,
                            }}
                          >
                            <Check size={12} /> Apply to Document
                          </button>
                        )}
                        {!msg.modifiedDocument && (
                          <button
                            className="btn btn-ghost btn-sm"
                            onClick={() => handleAcceptResponse(msg.content)}
                            style={{ fontSize: '0.8125rem', padding: '2px 8px' }}
                          >
                            <Check size={12} /> Accept
                          </button>
                        )}
                        <button
                          className="btn btn-ghost btn-sm"
                          onClick={() => handleCopyResponse(msg.modifiedDocument || msg.content)}
                          style={{ fontSize: '0.8125rem', padding: '2px 8px' }}
                        >
                          <Copy size={12} /> Copy
                        </button>
                      </div>
                    )}
                  </div>
                ))}

                {aiLoading && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.75rem',
                    color: 'var(--color-text-secondary)',
                    fontSize: '0.875rem',
                  }}>
                    <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                    AI is thinking...
                  </div>
                )}

                <div ref={chatEndRef} />
              </div>

              {/* Input area */}
              <div style={{
                padding: '0.75rem',
                borderTop: '1px solid var(--color-border)',
                flexShrink: 0,
              }}>
                <AttachmentChips attachments={att.attachments} onRemove={att.remove} disabled={aiLoading} />
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <input
                    ref={att.fileInputRef}
                    type="file"
                    multiple
                    accept={ATTACHMENT_ACCEPT}
                    onChange={att.onFileInputChange}
                    style={{ display: 'none' }}
                  />
                  <button
                    type="button"
                    className="btn btn-secondary btn-icon"
                    onClick={att.openFilePicker}
                    disabled={aiLoading}
                    title="Attach files or images"
                    style={{ width: 36, height: 36, flexShrink: 0, alignSelf: 'flex-end' }}
                  >
                    <Paperclip size={14} />
                  </button>
                  <textarea
                    value={aiInput}
                    onChange={(e) => setAiInput(e.target.value)}
                    {...att.inputHandlers}
                    onKeyDown={handleKeyDown}
                    placeholder="e.g. Make this section more detailed..."
                    rows={2}
                    disabled={aiLoading}
                    style={{
                      flex: 1,
                      resize: 'none',
                      padding: '0.5rem 0.75rem',
                      fontSize: '0.875rem',
                      background: 'var(--color-bg)',
                      border: '1px solid var(--color-border)',
                      borderRadius: 'var(--radius-md)',
                      color: 'var(--color-text)',
                      fontFamily: 'var(--font-primary)',
                      lineHeight: 1.4,
                    }}
                  />
                  <button
                    className="btn btn-primary btn-icon"
                    onClick={handleAiSubmit}
                    disabled={aiLoading || (!aiInput.trim() && att.attachments.length === 0)}
                    style={{ width: 36, height: 36, flexShrink: 0, alignSelf: 'flex-end' }}
                  >
                    <Send size={14} />
                  </button>
                </div>
                <p style={{
                  fontSize: '0.8125rem',
                  color: 'var(--color-text-secondary)',
                  margin: '0.375rem 0 0',
                  opacity: 0.7,
                }}>
                  Describe what to change. Optionally select text to focus edits. Enter to send.
                </p>
              </div>
            </>
          )}
        </div>
      )}

      {/* Agent Stream Panel for app generation (Claude Code mode) */}
      {agentAppPanelOpen && generationMode === 'claude_code' && (
        <div style={{
          width: 380,
          flexShrink: 0,
          borderLeft: '1px solid var(--color-border)',
          display: 'flex',
          flexDirection: 'column',
          animation: 'slideInRight 250ms ease-out',
        }}>
          <AgentStreamPanel
            messages={agent.messages}
            isStreaming={agent.isStreaming}
            wsStatus={agent.wsStatus}
            error={agent.error}
            agentProgress={agent.agentProgress}
            onCancel={() => {
              agent.cancelTask()
              setGeneratingApp(false)
            }}
            onClear={agent.clearMessages}
            onClose={() => {
              setAgentAppPanelOpen(false)
              setGeneratingApp(false)
            }}
            onPermissionResponse={agent.respondToPermission}
            title="Generating Application"
          />
        </div>
      )}
    </div>
  )
}
